import { Order, WithdrawalReceipt } from '../utils/types';

// #149: what the portal keeps of a withdrawal in this tab (sessionStorage,
// never the storage shared with other tabs):
// - `withdrawal.key:<shopId>:<orderId>` = { key, name }: the idempotency key of
//   the declaration form and the name of the last attempted POST, so a lost
//   response + reload retries with the same key and the same canonical hash;
// - `withdrawal.done:<shopId>:<orderId>` = receipt id: written first on a 201,
//   so a form re-entered for that order recovers the receipt;
// - `withdrawal.ctx:<id>` = the continuation context of the receipt page.
// Every access is guarded: without storage the portal still works, it only
// loses the recovery after a reload.

export interface WithdrawalContext {
  receipt: WithdrawalReceipt;
  order?: Order | null;
  proof?: string | null;
  shop?: string | null;
  standalone?: boolean;
}

export interface WithdrawalDraft {
  key: string;
  name: string;
}

const read = <T>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
};
const write = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // no storage: nothing to recover after a reload
  }
};
const remove = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // no storage
  }
};

const draftKey = (shopId: number, orderId: number) =>
  `withdrawal.key:${shopId}:${orderId}`;
const doneKey = (shopId: number, orderId: number) =>
  `withdrawal.done:${shopId}:${orderId}`;
const contextKey = (id: number) => `withdrawal.ctx:${id}`;

export const loadDraft = (
  shopId: number,
  orderId: number
): WithdrawalDraft | null => {
  const draft = read<Partial<WithdrawalDraft>>(draftKey(shopId, orderId));
  return draft &&
    typeof draft.key === 'string' &&
    draft.key !== '' &&
    typeof draft.name === 'string'
    ? { key: draft.key, name: draft.name }
    : null;
};
export const saveDraft = (
  shopId: number,
  orderId: number,
  draft: WithdrawalDraft
): void => write(draftKey(shopId, orderId), draft);
export const clearDraft = (shopId: number, orderId: number): void =>
  remove(draftKey(shopId, orderId));

export const markDone = (
  shopId: number,
  orderId: number,
  receiptId: number
): void => write(doneKey(shopId, orderId), receiptId);
export const doneReceiptId = (
  shopId: number,
  orderId: number
): number | null => {
  const id = read<unknown>(doneKey(shopId, orderId));
  return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
};

const isReceiptOf = (receipt: unknown, id: number): boolean =>
  !!receipt &&
  typeof receipt === 'object' &&
  (receipt as { id?: unknown }).id === id;

export const saveContext = (context: WithdrawalContext): void =>
  write(contextKey(context.receipt.id), context);

// the stored context of declaration `id` with the router state merged on top
// (a field the state does not carry never hides a stored one); null when
// neither has the receipt of `id`
export const resolveContext = (
  id: number,
  state: unknown
): WithdrawalContext | null => {
  const stored = read<WithdrawalContext>(contextKey(id));
  const base: Partial<WithdrawalContext> =
    stored && isReceiptOf(stored.receipt, id) ? stored : {};
  const merged: Partial<WithdrawalContext> = { ...base };
  if (state && typeof state === 'object') {
    const s = state as Partial<WithdrawalContext>;
    if (s.receipt === undefined || isReceiptOf(s.receipt, id)) {
      (['receipt', 'order', 'proof', 'shop', 'standalone'] as const).forEach(
        field => {
          if (s[field] !== undefined) {
            (merged as Record<string, unknown>)[field] = s[field];
          }
        }
      );
    }
  }
  return merged.receipt ? (merged as WithdrawalContext) : null;
};

// #154: the single entry (the order search) of the same storefront, proof
// and shop kept
export const portalEntryUrl = (
  proof: string | null | undefined,
  shop: string | null | undefined
): string => {
  const params = new URLSearchParams();
  if (proof) params.set('proof', proof);
  if (shop) params.set('shop', shop);
  const query = params.toString();
  return query ? `/order-form-page?${query}` : '/order-form-page';
};
